import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import Input from "components/foundation/Input/Input";
import CreateDocumentFormField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField";
import Button from "components/foundation/Button";
import { useNavigate } from "react-router-dom";
import Modal from "components/foundation/Modal/Modal";
import Toast, { useToast } from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import { useApprovalActionMutation } from "features/document/hooks/mutations/useApprovalActionMutation";
import { DOCUMENT_DETAIL_FIELD as FIELD } from "config/documentConstant";
import DocumentDetailFooter from "../DocumentDetailFooter";
import { useSetRecoilState } from "recoil";
import { showApproverSignState } from "store/document/documentCommon";
import Label from "components/foundation/Label/Label";
import styled from "styled-components";
import ReferenceUserEditModal from "components/document/edlt/ReferenceUserEdit";
import { ReferenceUserDto } from "apis/data-contracts";
import Divider from "components/foundation/Divider";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import useApproveRequest from "features/document/hooks/create/useApproveRequest";
import { DOCUMENT_CATEGORY_UUID } from "config/constant";
import CommentTextArea from "components/foundation/TextArea/CommentTextArea";

export interface DocumentDetailFooterForDecisionManagerProps {
  documentUuid: string;
  setBeforeUnloadDisable: (disable: boolean) => void;
  uploadDocPdfAndGetS3Url: (
    fileName: string,
    approveComment?: string,
  ) => Promise<string | undefined>;
  referenceUsers?: ReferenceUserDto[];
  permissionType?: "OWNER" | "APPROVER" | "REFERRER" | "READER";
  documentApprove: string;
  author: string;
  comment: string;
  approvedUser?: string;
  isAnalysis?: boolean;
}

interface DocumentDetailFooterForm {
  [FIELD.COMMENT.key]: string;
  [FIELD.REFERENCE_USERS.key]: string[];
}

const DocumentDetailFooterForDecisionManager = ({
  documentUuid,
  setBeforeUnloadDisable,
  uploadDocPdfAndGetS3Url,
  referenceUsers,
  permissionType,
  documentApprove,
  author,
  comment,
  approvedUser,
  isAnalysis,
}: DocumentDetailFooterForDecisionManagerProps) => {
  const { handleSubmit, control, register } =
    useFormContext<DocumentDetailFooterForm>();

  const isReqSupplement = documentApprove === "REQ_SUPPLEMENT";

  // Analysis 문서이면서 1단계 검토중, 2단계 검토중인 경우, 참조자 수정 X
  const isAnalysInProgress =
    isAnalysis &&
    (documentApprove === "REVIEW_IN_PROGRESS" ||
      documentApprove === "APPROVE_IN_PROGRESS");

  // 1단계 승인 요청
  const isAnalysisReview =
    isAnalysis && documentApprove === "REVIEW_IN_PROGRESS";

  const navigate = useNavigate();

  const navigateBackToDesk = () => {
    const canGoBack =
      typeof window !== "undefined" &&
      typeof window.history !== "undefined" &&
      window.history.length > 1;

    if (canGoBack) {
      navigate(-1);
      return;
    }

    if (isAnalysis) {
      navigate(
        `${ROUTER_PATH.myPage.desk}?selectedTab.uuid=${DOCUMENT_CATEGORY_UUID.ANALYSIS}`,
      );
      return;
    }

    navigate(ROUTER_PATH.myPage.desk);
  };

  /* 토스트 */
  const {
    open: openToast,
    onOpenChange: onOpenChangeToast,
    onOpenToast,
  } = useToast();

  const [toastMessage, setToastMessage] = useState<string | undefined>();

  /* 보완요청 */
  const [openSupplementModal, setOpenSupplementModal] = useState(false);

  const approvalActionMutation = useApprovalActionMutation();

  /**
   * @description 보완요청 함수
   */
  const requestSupplement = handleSubmit((form: DocumentDetailFooterForm) => {
    approvalActionMutation.mutate(
      {
        documentUuid,
        request: {
          referenceUserUuids: newReferenceUsersList.map((user) => user.uuid),
          action: "REQ_SUPPLEMENT",
          comment: form[FIELD.COMMENT.key],
        },
      },
      {
        onSuccess: () => {
          setToastMessage("해당 문서의 보완 요청이 완료되었습니다.");
          onOpenToast(() => {
            setBeforeUnloadDisable(true);
            navigateBackToDesk();
          });
        },
      },
    );
  });

  /**
   * @description 보완요청 버튼 클릭 이벤트 핸들러
   */
  const onClickRequestSupplement = () => {
    setOpenSupplementModal(true);
  };

  /* 승인 */
  const [openApprovalModal, setOpenApprovalModal] = useState(false);

  const setShowApproverSign = useSetRecoilState(showApproverSignState);

  /**
   * @description 승인 함수
   */
  const approve = handleSubmit(async (form: DocumentDetailFooterForm) => {
    setShowApproverSign(true);

    setTimeout(async () => {
      // 승인 시 입력한 코멘트를 포함하여 PDF 생성
      const approveComment = form[FIELD.COMMENT.key];
      const s3Url = await uploadDocPdfAndGetS3Url("octo.pdf", approveComment);

      approvalActionMutation.mutate(
        {
          documentUuid,
          request: {
            referenceUserUuids: newReferenceUsersList.map((user) => user.uuid),
            action: isAnalysisReview ? "REVIEW" : "APPROVE",
            comment: approveComment,
            documentFileUrl: s3Url,
          },
        },
        {
          onSuccess: async () => {
            setToastMessage("해당 문서의 승인이 완료되었습니다.");
            setBeforeUnloadDisable(true);
            onOpenToast(() => {
              setShowApproverSign(false);
              navigateBackToDesk();
            });
          },
          onError: () => {
            setShowApproverSign(false);
          },
        },
      );
    }, 10);
  });

  /**
   * @description 승인 버튼 클릭 이벤트 핸들러
   */
  const onClickApprove = () => {
    setOpenApprovalModal(true);
  };

  // 대표자 또는 전결권자 계정만 참조자 수정 가능
  const { data: currentUser } = useUserDetailQuery();
  const hasAuthorityToEditReferenceUser =
    currentUser?.adminType === "SUPERADMIN" || permissionType === "APPROVER";

  const [newReferenceUsersList, setNewReferenceUsersList] = useState<
    ReferenceUserDto[]
  >([]);

  // 모달에서 임시로 사용할 상태 (확인 버튼을 눌렀을 때만 실제 상태에 반영)
  const [tempReferenceUsersList, setTempReferenceUsersList] = useState<
    ReferenceUserDto[]
  >([]);

  useEffect(() => {
    setNewReferenceUsersList(referenceUsers || []);
  }, [referenceUsers]);

  const [openReferenceUserEditModal, setOpenReferenceUserEditModal] =
    useState(false);

  const { updateReferenceUser } = useApproveRequest();

  return (
    <>
      <DocumentDetailFooter
        fields={
          <>
            <CreateDocumentFormField label={FIELD.COMMENT.label}>
              {isReqSupplement ? (
                <>
                  <Input
                    className="pc-only"
                    placeholder={FIELD.COMMENT.placeholder}
                    readOnly
                    value={comment}
                  />
                  <CommentTextArea
                    className="mobile-only"
                    fill
                    text={comment}
                    placeholder={FIELD.COMMENT.placeholder}
                    readOnly
                  />
                </>
              ) : (
                <>
                  <Controller
                    name={FIELD.COMMENT.key}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Input
                        className="pc-only"
                        placeholder={FIELD.COMMENT.placeholder}
                        {...field}
                      />
                    )}
                  />
                  <CommentTextArea
                    className="mobile-only"
                    fill
                    text={isReqSupplement ? comment : ""}
                    placeholder={FIELD.COMMENT.placeholder}
                    readOnly={isReqSupplement}
                    {...register(FIELD.COMMENT.key)}
                  />
                </>
              )}
            </CreateDocumentFormField>

            <CreateDocumentFormField
              label="참조자"
              bodyStyle={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <ReferenceUserWrapStyle>
                {newReferenceUsersList?.map((referenceUser) => (
                  <Label key={referenceUser.uuid} variant="grey">
                    {referenceUser?.name}
                  </Label>
                ))}
              </ReferenceUserWrapStyle>

              {/* 대표이사, 전결권자 수정 가능 */}
              {!isAnalysInProgress && hasAuthorityToEditReferenceUser && (
                <Button
                  variant="tertiary"
                  size="small"
                  fill={false}
                  style={{
                    width: "auto",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    // 모달 열 때 현재 참조자 목록을 임시 상태에 복사
                    setTempReferenceUsersList([...newReferenceUsersList]);
                    setOpenReferenceUserEditModal(true);
                  }}
                >
                  수정
                </Button>
              )}
            </CreateDocumentFormField>
          </>
        }
        actions={
          <>
            <Button
              disabled={isReqSupplement}
              variant="tertiary-neg"
              size="large"
              onClick={onClickRequestSupplement}
            >
              보완요청
            </Button>

            <Button
              disabled={isReqSupplement}
              variant="primary"
              size="large"
              onClick={onClickApprove}
            >
              승인
            </Button>
          </>
        }
      />

      <Toast.Message open={openToast} onOpenChange={onOpenChangeToast}>
        {toastMessage}
      </Toast.Message>

      <Modal.Root
        open={openSupplementModal}
        onOpenChange={setOpenSupplementModal}
      >
        <Modal.Body
          title='작성된 내용으로 "보완 요청"을 진행하시겠습니까?'
          onConfirm={() => requestSupplement()}
          onCancel={() => setOpenSupplementModal(false)}
        >
          보완 요청시 작성자에게 푸시알림이 발송됩니다.
        </Modal.Body>
      </Modal.Root>

      <Modal.Root open={openApprovalModal} onOpenChange={setOpenApprovalModal}>
        <Modal.Body
          title="문서를 승인 하시겠습니까?"
          onConfirm={() => approve()}
          onCancel={() => setOpenApprovalModal(false)}
        >
          문서 승인시 설정한 공개범위에 맞춰 문서가 리스트에 업로드됩니다.
        </Modal.Body>
      </Modal.Root>

      <Modal.Root
        open={openReferenceUserEditModal}
        onOpenChange={setOpenReferenceUserEditModal}
      >
        <Modal.Body
          buttonContainerStyle={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
          style={{
            overflow: "visible",
          }}
          overflowVisible={true}
          onConfirm={() => {
            // 임시 상태를 실제 상태에 반영
            updateReferenceUser({
              documentUuid,
              referenceUserUuids: tempReferenceUsersList.map(
                (user) => user.uuid,
              ),
              permissionType,
              currentStatus: documentApprove,
              successCallback: () => {
                setNewReferenceUsersList([...tempReferenceUsersList]);
                setOpenReferenceUserEditModal(false);
              },
            });
          }}
          onCancel={() => {
            // 취소 시 모달만 닫기
            setOpenReferenceUserEditModal(false);
          }}
        >
          <ReferenceUserEditModal
            referenceUsers={tempReferenceUsersList || []}
            onChangeReferenceUsers={(referenceUsers) => {
              setTempReferenceUsersList(referenceUsers);
            }}
            author={author}
            approvedUser={approvedUser}
          />
          <Divider />
        </Modal.Body>
      </Modal.Root>
    </>
  );
};

export default DocumentDetailFooterForDecisionManager;

const ReferenceUserWrapStyle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.25rem;

  ${({ theme }) => theme.media.small} {
    gap: 0.6rem;
  }
`;
